import { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Divider, MenuItem, Stack, TextField, Typography } from '@mui/material';
import type { BookingStage, BookingChanges, BookingCollaboration, BookingDecision, BookingTerms, Hold, SharedSettlement } from 'api-client/models';
import { useHoldsApi, useStagesApi } from '@/context/AppContext';
import { BookingTermsFields, emptyBookingTerms } from './BookingTermsFields';
import { SharedSettlementFields } from './SharedSettlementFields';
import { bookingError } from '../utils/bookingError';
import { BookingChangeSummary } from './BookingChangeSummary';
import { apiDateToDateOnly } from '@/utils/dateUtils';

type ViewProps = {
  stages?: BookingStage[]; hold: Hold; collaboration: BookingCollaboration; pending: boolean; error: string;
  onDecision: (decision: Omit<BookingDecision, 'revision'>) => void;
  onUpdate: (changes: BookingChanges) => void;
};

export function BookingWorkflowView({ stages = [], hold, collaboration, pending, error, onDecision, onUpdate }: ViewProps) {
  const [terms, setTerms] = useState<BookingTerms>(hold.bookingTerms?.dealType ? hold.bookingTerms : emptyBookingTerms);
  const [settlement, setSettlement] = useState<SharedSettlement>(hold.sharedSettlement ?? {});
  const [date, setDate] = useState(apiDateToDateOnly(hold.holdDate));
  const [stage, setStage] = useState(hold.stage);
  const [showTerms, setShowTerms] = useState(false);
  const [note, setNote] = useState('');
  const host = collaboration.canManage;
  const requested = hold.status === 'requested';
  const active = (host || collaboration.canRequestChanges) && ['pending', 'challenged', 'standby', 'confirmed'].includes(hold.status ?? '');
  const submitChanges = (changes: BookingChanges): void => {
    if (host) onUpdate(changes);
    else onDecision({ operation: 'request_change', changes });
  };
  return <Card sx={{ my: 3 }}><CardContent><Stack spacing={2}>
    <Typography component="h2" variant="h5">Shared booking</Typography>
    {requested && <Alert severity="info">Awaiting host acceptance. No queue position or date is reserved.</Alert>}
    {!host && active && <Alert severity="info">The host controls this booking. Submit a request to change it.</Alert>}
    {hold.status === 'declined' && <Alert severity="warning">The host declined this request. No date was reserved.</Alert>}
    {hold.bookingTerms?.dealType ? <Typography>
      {hold.bookingTerms.dealType === 'flat_rental'
        ? `Flat rental: ${hold.bookingTerms.currency} ${hold.bookingTerms.rentalFee}`
        : `Revenue sharing: host ${hold.bookingTerms.hostPercentage}%, ${hold.bookingTerms.settlementBasis === 'net_ticket_sales' ? 'ticket sales minus agreed expenses' : 'gross ticket sales'}`}
    </Typography> : <Typography color="text.secondary">No shared deal terms recorded.</Typography>}
    {collaboration.lineup.length > 0 && <Box>
      <Typography component="h3" variant="h6">Lineup</Typography>
      {collaboration.lineup.map((artist, index) => <Typography key={String(artist.performer_id ?? index)}>{String(artist.performer__performer_name ?? '')}</Typography>)}
    </Box>}
    {requested && host && <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
      <Button disabled={pending} variant="contained" onClick={() => onDecision({ operation: 'accept' })}>Accept and reserve</Button>
      <Button disabled={pending} onClick={() => onDecision({ operation: 'decline' })}>Decline request</Button>
    </Stack>}
    {active && <>
      <Stack direction="row" useFlexGap flexWrap="wrap" gap={1}>
        {['pending', 'challenged'].includes(hold.status ?? '') && <Button disabled={pending} onClick={() => submitChanges({ action: 'confirm' })}>{host ? 'Confirm booking' : 'Request confirmation'}</Button>}
        <Button disabled={pending || hold.status === 'confirmed'} onClick={() => submitChanges({ action: 'release' })}>{host ? 'Release booking' : 'Request release'}</Button>
        {hold.status === 'pending' && hold.holdPosition > 1 && <Button disabled={pending} onClick={() => submitChanges({ action: 'challenge' })}>{host ? 'Start challenge' : 'Request challenge'}</Button>}
      </Stack>
      {hold.status === 'confirmed' && <Typography color="text.secondary">Confirmed bookings cannot be released through the hold queue.</Typography>}
      <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
        <TextField label="Booking date" type="date" value={date} disabled={pending} InputLabelProps={{ shrink: true }} onChange={event => setDate(event.target.value)} />
        <TextField select label="Booking stage" value={stage} disabled={pending || !stages.length} onChange={event => setStage(event.target.value)} helperText="Choose a stage operated by this host.">
          {!stages.some(item => item.id === hold.stage) && <MenuItem value={hold.stage}>{hold.stageName}</MenuItem>}
          {stages.map(item => <MenuItem key={item.id} value={item.id}>{item.venueName} — {item.name}</MenuItem>)}
        </TextField>
        <Button disabled={pending || (date === apiDateToDateOnly(hold.holdDate) && stage === hold.stage)} onClick={() => submitChanges({ action: 'update', holdDate: new Date(`${date}T00:00:00Z`), stage })}>{host ? 'Save date / stage' : 'Request date / stage'}</Button>
      </Stack>
    </>}
    <Button onClick={() => setShowTerms(!showTerms)} disabled={pending}>{showTerms ? 'Hide terms' : 'Review deal terms'}</Button>
    {showTerms && <>
      <BookingTermsFields value={terms} onChange={setTerms} disabled={pending || !active} />
      {active && <Typography variant="body2" color="text.secondary">Changing the deal type, currency or revenue-share basis resets shared settlement figures. Payment history remains in the booking decisions.</Typography>}
      {active && <Button disabled={pending} onClick={() => submitChanges({ action: 'update', bookingTerms: terms })}>{host ? 'Save shared terms' : 'Request terms change'}</Button>}
    </>}
    {hold.bookingTerms?.dealType && <>
      <Divider /><Typography component="h3" variant="h6">Shared settlement</Typography>
      <SharedSettlementFields terms={hold.bookingTerms} value={settlement} onChange={setSettlement} disabled={pending || !host || !active} />
      {host && active && <Button disabled={pending} onClick={() => onUpdate({ action: 'update', sharedSettlement: settlement })}>Save shared settlement</Button>}
    </>}
    <Divider /><Typography component="h3" variant="h6">Change requests</Typography>
    {!collaboration.requests.length && <Typography color="text.secondary">No change requests.</Typography>}
    {host && <TextField label="Decision note (shared)" value={note} disabled={pending} onChange={event => setNote(event.target.value)} />}
    {collaboration.requests.map(item => <Box key={item.id} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
      <Typography>{item.status} — {item.changes?.action ?? 'Booking update'}</Typography>
      <BookingChangeSummary changes={item.changes ?? {}} stages={stages} />
      {item.decisionNote && <Typography>{item.decisionNote}</Typography>}
      {host && item.status === 'pending' && <Stack direction="row" spacing={1}>
        <Button disabled={pending || item.baseRevision !== collaboration.revision} onClick={() => onDecision({ operation: 'approve_change', requestId: item.id, decisionNote: note })}>Approve change</Button>
        <Button disabled={pending} onClick={() => onDecision({ operation: 'reject_change', requestId: item.id, decisionNote: note })}>Decline change</Button>
      </Stack>}
      {item.status === 'pending' && item.baseRevision !== collaboration.revision && <Typography color="text.secondary">The booking changed. A new request is required.</Typography>}
    </Box>)}
    {pending && <Typography role="status">Saving booking…</Typography>}
    {error && <Alert severity="error">{error}</Alert>}
  </Stack></CardContent></Card>;
}

export default function BookingWorkflowPanel({ hold, onSaved }: { hold: Hold; onSaved: (hold: Hold) => void }) {
  const api = useHoldsApi();
  const stagesApi = useStagesApi();
  const [stages, setStages] = useState<BookingStage[]>([]);
  const [collaboration, setCollaboration] = useState<BookingCollaboration | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let current = true;
    stagesApi.stagesBookingDirectory().then(result => {
      if (current) setStages(result.filter(item => item.hostTeam === hold.hostTeam));
    }).catch(() => { if (current) setError('Stage choices are unavailable. Reload before changing stages.'); });
    api.holdsCollaboration({ id: hold.id }).then(result => { if (current) setCollaboration(result); })
      .catch(() => { if (current) setError('Unable to load booking permissions. Reload to try again.'); });
    return () => { current = false; };
  }, [api, stagesApi, hold.id, hold.updatedAt]);
  const run = async (operation: () => Promise<unknown>): Promise<void> => {
    setPending(true); setError('');
    try {
      await operation();
      const [saved, details] = await Promise.all([api.holdsRetrieve({ id: hold.id }), api.holdsCollaboration({ id: hold.id })]);
      onSaved(saved); setCollaboration(details);
    } catch (reason) {
      setError(await bookingError(reason));
    } finally { setPending(false); }
  };
  if (!collaboration) return <Alert severity={error ? 'error' : 'info'}>{error || 'Loading booking permissions…'}</Alert>;
  return <BookingWorkflowView key={`${hold.id}-${hold.bookingRevision}`} stages={stages} hold={hold} collaboration={collaboration} pending={pending} error={error}
    onDecision={decision => { void run(() => api.holdsBookingDecision({ id: hold.id, bookingDecision: { ...decision, revision: collaboration.revision } })); }}
    onUpdate={changes => { void run(() => api.holdsBookingDecision({ id: hold.id, bookingDecision: {
      operation: 'apply_change', changes, revision: collaboration.revision,
    } })); }} />;
}
